/**
 * uPlot docs: https://github.com/leeoniya/uPlot/tree/master/docs */
import type { ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { isNil, isString } from '@pct/ts-typeguard';
import type uPlot from 'uplot';
import type { RawClientDataDTO } from '../mind-uplot/model/raw-client-data.dto';
import { MindUplotComponent } from '../mind-uplot/mind-uplot.component';
import { PlaybackStateService } from '../../../features/session/playback-state.service';
import type { DisplayedMarker } from '../../models/displayed-marker';

@Component({
  selector: 'app-mind-uplot-playback',
  standalone: true,
  template: `<div #chartContainer class="chart-container" [class.cursor-centered]="isCursorCentered()"></div>`,
  styleUrls: ['./mind-uplot-playback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MindUplotPlaybackComponent extends MindUplotComponent {
  readonly frames = input.required<RawClientDataDTO[] | null>();
  readonly markers = input<DisplayedMarker[]>([]);
  // kulcs id az összetartozó résztvevők kurzorának szinkronban tartásához
  readonly cursorSyncKey = input<string | null>(null);

  protected readonly isCursorCentered = signal<boolean>(false);
  private readonly playbackState = inject(PlaybackStateService);
  // mouse enter/leave eseményekhez, hogy mutatjuk-e a kurzort;
  // pending: egér rajta van -> lejátszás stop -> láthatóra állítjuk
  private cursorState: 'hidden' | 'pending' | 'visible' = 'hidden';

  readonly viewWindow = computed(() => this.componentOptions().viewWindow ?? this.playbackState.zoomLevel() ?? 10000); //ms

  private isSettingCursor = false;

  constructor() {
    super();

    effect(() => {
      const frames = this.frames();
      if (frames !== null) {
        this.drawPointsWithoutMovingViewport(frames);
      }
    });

    effect(() => {
      const headMs = this.playbackState.displayHead();
      this.playbackState.resetZoomTrigger();

      if (isNil(this.uPlotInstance)) return;

      const half = this.viewWindow() / 2;
      this.uPlotInstance.setScale('x', { min: headMs - half, max: headMs + half });
    });

    effect(() => {
      const isPlaying = this.playbackState.isPlaying();
      if (!isPlaying && this.cursorState === 'pending') {
        this.cursorState = 'visible';
      }
      if (isPlaying && this.cursorState === 'visible') {
        this.cursorState = 'pending';
      }
    });

    effect(() => {
      const _markers = this.markers();
      // uPlot megosztott cursor-sync regisztere (cursorSyncKey) inkonzisztens állapotba kerülhet,
      // ha egy másik, azonos sync-kulcsú plot épp destroy() alatt van ugyanebben a change detection
      // ciklusban (participant eltávolításakor). Race condition-t feloldja a requestAnimationFrame
      requestAnimationFrame(() => {
        this.uPlotInstance?.redraw();
      });
    });
  }

  protected getDefaultOptions(container: ElementRef): uPlot.Options {
    const cursorSyncKey = this.cursorSyncKey();
    return {
      width: container.nativeElement.offsetWidth,
      height: 400,
      cursor: {
        drag: { setScale: true, x: true, y: false },
        points: { show: false },
        // Kikapcsoljuk, hogy az egérmozgás felülírhassa a fix középállást
        move: (_u: uPlot, left: number, top: number) => [left, top],
        bind: {
          dblclick: () => null,
        },
        ...(cursorSyncKey !== null && {
          sync: {
            key: cursorSyncKey,
          },
        }),
      },
      scales: {
        x: { time: false },
        y: { auto: false, range: [-500, 500] },
      },
      hooks: {
        setCursor: [
          u => {
            if (this.isSettingCursor) return;

            const centerPx = u.valToPos(this.playbackState.displayHead(), 'x');
            const cursorLeft = u.cursor.left ?? -1;

            // Centered: ha az egér nincs a diagramon, vagy a kurzor a playhead-en áll
            const isCentered = cursorLeft < 0 || Math.abs(cursorLeft - centerPx) < 2;
            this.isCursorCentered.set(isCentered);

            if (cursorLeft < 0) {
              this.isSettingCursor = true;
              u.setCursor({ left: centerPx, top: u.cursor.top ?? 0 });
              this.isSettingCursor = false;
            }

            // tooltip adatok beállítás
            // csak a hover diagram és sync-elt párja érdekel
            if (this.playbackState.isPlaying() || cursorSyncKey !== this.playbackState.cursorTooltip().deviceId) {
              return;
            }

            const idx = u.cursor.idx;
            if (isNil(idx)) return;

            const label = isString(u.series[1].label) ? u.series[1].label : '';
            const xVal = u.data[0][idx];
            const yVal = u.data[1]?.[idx] ?? null;

            this.playbackState.cursorTooltip.update(t => ({
              xMs: xVal,
              deviceId: cursorSyncKey,
              values: {
                ...t?.values,
                ...(cursorSyncKey === null ? {} : { [label]: yVal }),
              },
            }));

            // csak a hover diagram állítja a tooltip pozíciót
            if (this.cursorState === 'visible') {
              const rect = u.over.getBoundingClientRect();
              this.playbackState.cursorClientPos.set({
                x: rect.left + cursorLeft,
                y: rect.top + (u.cursor.top ?? 0),
              });
            }
          },
        ],
        draw: [
          u => {
            if (this.isSettingCursor) return;

            const centerValue = this.playbackState.displayHead();
            const centerPx = u.valToPos(centerValue, 'x');

            if (centerPx !== u.cursor.left) {
              this.isSettingCursor = true;
              const top = this.playbackState.isPlaying() ? -1 : (u.cursor.top ?? 0);
              u.setCursor({ left: centerPx, top });
              this.isSettingCursor = false;
            }

            const markerDeltas = new Set<number>();
            const markers = this.markers();
            if (markers.length > 0) {
              const { ctx, bbox } = u;
              ctx.save();
              ctx.lineWidth = 1;

              const top = bbox.top;
              const height = bbox.height;

              for (const marker of markers) {
                if (markerDeltas.has(marker.delta)) {
                  continue;
                }
                markerDeltas.add(marker.delta);
                // X pozíció kiszámítása az időbélyegből (pixelben)
                const px = Math.round(u.valToPos(marker.delta, 'x', true));
                // Csak akkor rajzoljuk ki, ha a jelenlegi nézetben (bbox) látható
                if (px < bbox.left || px > bbox.left + bbox.width) continue;

                switch (marker.displayType) {
                  case 'ImportPreview':
                    ctx.strokeStyle = '#1ad7d7';
                    ctx.setLineDash([8, 8]);
                    break;
                  case 'ImportConflict':
                    ctx.strokeStyle = '#fc4c4b';
                    break;
                  case 'SelectedTestResult':
                    ctx.strokeStyle = '#1ad7d7';
                    break;
                  case 'Default':
                  default:
                    ctx.strokeStyle = '#56ff00';
                }
                ctx.beginPath();
                ctx.moveTo(px, top);
                ctx.lineTo(px, top + height);
                ctx.stroke();
              }

              ctx.restore();
            }
          },
        ],
        init: [
          u => {
            u.over.addEventListener('dblclick', () => {
              this.playbackState.resetZoom();
            });
            u.over.addEventListener('mouseenter', () => {
              this.cursorState = this.playbackState.isPlaying() ? 'pending' : 'visible';
              this.playbackState.cursorTooltip.update(t => ({ ...t, deviceId: cursorSyncKey }));
            });
            u.over.addEventListener('mouseleave', () => {
              if (cursorSyncKey === this.playbackState.cursorTooltip().deviceId) {
                this.cursorState = 'hidden';
                this.playbackState.cursorTooltip.update(t => ({ ...t, deviceId: null }));
              }
            });
          },
        ],
      },
      series: [
        {
          label: this.translocoService.translate('shared.uPlot.deltaTime'),
          value: (_self, ticks) => ticks / 1000 + 's',
        },
        {
          stroke: '#fc4c4b',
          width: 1,
          label: this.translocoService.translate('shared.uPlot.value'),
        },
      ],
      axes: [
        {
          stroke: '#eee',
          values: (_self, ticks) => ticks.map(rawValue => rawValue / 1000 + 's'),
        },
        { stroke: '#eee' },
      ],
    };
  }

  private drawPointsWithoutMovingViewport(points: RawClientDataDTO[]): void {
    this.addPointsToSeries(points);

    if (isNil(this.uPlotInstance)) return;

    this.uPlotInstance.setData(this.data, false);
    this.uPlotInstance.redraw();
  }

  protected resetForNewSession(): void {
    if (!isNil(this.timer)) {
      cancelAnimationFrame(this.timer);
      this.timer = undefined;
    }
    this.reset();
    this.latestDelta = 0;
  }

  resize(): void {
    const container = this.container();
    if (isNil(this.uPlotInstance) || isNil(container)) return;
    this.uPlotInstance.setSize({
      width: container.nativeElement.offsetWidth,
      height: this.uPlotInstance.height,
    });
  }
}
